'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components';
import { buildContactSupportHandoffUrl } from '@/lib/ux-reference-id';
import {
  ARIA_COPY_REFERENCE_ID,
  ARIA_DISMISS,
  CTA_CONTACT_SUPPORT,
  CTA_COPY,
  CTA_COPIED,
  CTA_TRY_AGAIN,
  CTA_REVIEW_ORDER_DETAILS,
  STATUS_BUTTON_RETRYING,
  STATUS_RETRY_IN_PROGRESS,
} from '@/lib/ux-copy';

type Props = {
  title: string;
  message: string;
  referenceId: string;
  supportContext: 'checkout' | 'cart' | 'checkout-promo';
  onDismiss?: () => void;
  onRetry?: () => Promise<void>;
  retryLabel?: string;
  onEditDetails?: () => void;
  editDetailsLabel?: string;
  additionalActions?: ReactNode;
  className?: string;
};

export function CheckoutRecoveryError({
  title,
  message,
  referenceId,
  supportContext,
  onDismiss,
  onRetry,
  retryLabel = CTA_TRY_AGAIN,
  onEditDetails,
  editDetailsLabel = CTA_REVIEW_ORDER_DETAILS,
  additionalActions,
  className = '',
}: Props) {
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const supportHref = buildContactSupportHandoffUrl({ referenceId, context: supportContext });

  const scrollCardToTop = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    let scrollParent: HTMLElement | null = el.parentElement;
    while (scrollParent) {
      const style = window.getComputedStyle(scrollParent);
      const canScrollY =
        (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'overlay') &&
        scrollParent.scrollHeight > scrollParent.clientHeight;
      if (canScrollY) break;
      scrollParent = scrollParent.parentElement;
    }
    if (!scrollParent) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const parentTop = scrollParent.getBoundingClientRect().top;
    const nextTop = scrollParent.scrollTop + (el.getBoundingClientRect().top - parentTop);
    scrollParent.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => { scrollCardToTop(); });
    return () => cancelAnimationFrame(id);
  }, [referenceId, scrollCardToTop]);

  const copyId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(referenceId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [referenceId]);

  const handleRetry = useCallback(async () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
      requestAnimationFrame(() => { scrollCardToTop(); });
    }
  }, [onRetry, retrying, scrollCardToTop]);

  return (
    <div
      ref={cardRef}
      role="alert"
      className={`overflow-hidden rounded-xl border border-neutral-10 bg-constant-white shadow-sm ${className}`}
    >
      {/* Accent bar */}
      <div className="h-0.5 w-full bg-red-50" aria-hidden />

      <div className="px-4 pt-3 pb-3.5">
        {/* Header row */}
        <div className="flex items-start gap-2.5">
          {/* Icon badge */}
          <div className="mt-px flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-5 ring-1 ring-red-15">
            {retrying ? (
              <svg className="h-4 w-4 animate-spin text-red-50" fill="none" viewBox="0 0 24 24" aria-hidden>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              <svg className="h-4 w-4 text-red-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            )}
          </div>

          {/* Title + message */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight text-text-primary">
              {retrying ? STATUS_RETRY_IN_PROGRESS : title}
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-text-secondary">{message}</p>
          </div>

          {/* Dismiss */}
          {onDismiss && !retrying && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onDismiss}
              aria-label={ARIA_DISMISS}
              data-no-lift
              className="-mt-0.5 -mr-0.5 h-6 w-6 rounded-md"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          )}
        </div>

        {/* Action buttons */}
        {(onRetry || onEditDetails) && (
          <div className="mt-2.5 ml-[2.625rem] flex flex-wrap items-center gap-1.5">
            {onRetry && (
              <Button
                type="button"
                variant="brand"
                size="sm"
                onClick={() => void handleRetry()}
                disabled={retrying}
                startIcon={
                  retrying ? (
                    <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  ) : (
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )
                }
              >
                {retrying ? STATUS_BUTTON_RETRYING : retryLabel}
              </Button>
            )}
            {onEditDetails && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onEditDetails}
                disabled={retrying}
              >
                {editDetailsLabel}
              </Button>
            )}
          </div>
        )}

        {/* Reference ID, compact single row */}
        <div className="mt-2.5 ml-[2.625rem] flex items-center gap-2 rounded-lg border border-neutral-10 bg-neutral-5 px-3 py-2">
          <div className="min-w-0 flex-1 overflow-hidden">
            <span className="mr-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">Ref</span>
            <code className="font-mono text-[11px] text-text-secondary">{referenceId}</code>
          </div>
          <Button
            raw
            type="button"
            onClick={() => void copyId()}
            disabled={retrying}
            aria-label={copied ? CTA_COPIED : ARIA_COPY_REFERENCE_ID}
            data-no-lift
            className="shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-text-tertiary transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-40"
          >
            {copied ? (
              <>
                <svg className="h-3 w-3 text-green-60 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                {CTA_COPIED}
              </>
            ) : (
              <>
                <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                {CTA_COPY}
              </>
            )}
          </Button>
        </div>

        {/* Footer: contact support */}
        <div className="mt-2 ml-[2.625rem]">
          <Link
            href={supportHref}
            className="inline-flex items-center gap-1 text-[11px] text-text-tertiary transition-colors"
          >
            <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {CTA_CONTACT_SUPPORT}
          </Link>
        </div>

        {additionalActions && (
          <div className="mt-3 ml-[2.625rem] border-t border-neutral-10 pt-3">
            {additionalActions}
          </div>
        )}
      </div>
    </div>
  );
}
